import React, { useState } from 'react';
import { Card, Upload, Button, Space, Tag, message, Alert, Row, Col, Tabs, Table } from 'antd';
import { UploadOutlined, ReloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';
import { yuan } from '../utils/format.js';

export default function OrderVerify() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const parseFile = async (file) => {
    setLoading(true);
    const fd = new FormData();
    fd.append('file', file);
    const res = await apiFetch('/api/order-verify/parse', { method: 'POST', body: fd }).then(r => r.json());
    setLoading(false);
    if (res.success) { setResult(res.result); message.success('校验完成，结果见下方'); }
    else { setResult(null); message.error(res.error || '解析失败'); }
    return false;
  };

  const missingColumns = [
    { title: '订单号', dataIndex: 'order_no', width: 200 },
    { title: '店铺', dataIndex: 'shop', width: 140, ellipsis: true },
    { title: '商品', dataIndex: 'product_name', ellipsis: true },
    { title: '总表实收', dataIndex: 'bill_amount', width: 110, align: 'right', render: v => yuan(v) },
    { title: '总表运单号', dataIndex: 'bill_tracking', width: 160 },
  ];

  const amountColumns = [
    { title: '订单号', dataIndex: 'order_no', width: 200 },
    { title: '店铺', dataIndex: 'shop', width: 140, ellipsis: true },
    { title: '商品', dataIndex: 'product_name', ellipsis: true },
    { title: '总表实收', dataIndex: 'bill_amount', width: 110, align: 'right', render: v => yuan(v) },
    { title: '系统实收', dataIndex: 'sys_amount', width: 110, align: 'right', render: v => yuan(v) },
    { title: '差额', key: 'diff', width: 110, align: 'right',
      render: (_, r) => { const d = Math.round((r.bill_amount - r.sys_amount) * 100) / 100; return <span style={{ color: '#CF1322', fontWeight: 600 }}>{d > 0 ? '+' : ''}{yuan(d)}</span>; } },
  ];

  const trackColumns = [
    { title: '订单号', dataIndex: 'order_no', width: 200 },
    { title: '店铺', dataIndex: 'shop', width: 140, ellipsis: true },
    { title: '商品', dataIndex: 'product_name', ellipsis: true },
    { title: '总表运单号', dataIndex: 'bill_tracking', width: 160, render: v => <span style={{ color: '#CF1322', fontWeight: 600 }}>{v || '—'}</span> },
    { title: '系统运单号', dataIndex: 'sys_tracking', width: 160, render: v => v || <span style={{ color: '#bfbfbf' }}>（系统未填）</span> },
  ];

  const tabItems = result ? [
    {
      key: 'missing', label: <span>遗漏（总表有系统无）<Tag color="#CF1322" style={{ marginLeft: 6 }}>{result.missing_count}</Tag></span>,
      children: result.missing.length
        ? <Table rowKey="order_no" size="small" dataSource={result.missing} columns={missingColumns} scroll={{ x: 800 }} pagination={{ pageSize: 10 }} />
        : <Alert type="success" showIcon message="无遗漏，总表订单在系统里都能找到" />,
    },
    {
      key: 'amount', label: <span>金额错误 <Tag color="#CF1322" style={{ marginLeft: 6 }}>{result.amount_err_count}</Tag></span>,
      children: result.amountErr.length
        ? <Table rowKey="order_no" size="small" dataSource={result.amountErr} columns={amountColumns} scroll={{ x: 800 }} pagination={{ pageSize: 10 }} />
        : <Alert type="success" showIcon message="无金额错误，总表实收与系统一致" />,
    },
    {
      key: 'track', label: <span>单号错误 <Tag color="#EF9F27" style={{ marginLeft: 6 }}>{result.track_err_count}</Tag></span>,
      children: result.trackErr.length
        ? <Table rowKey="order_no" size="small" dataSource={result.trackErr} columns={trackColumns} scroll={{ x: 800 }} pagination={{ pageSize: 10 }} />
        : <Alert type="success" showIcon message="无单号错误，总表运单号与系统一致" />,
    },
  ] : [];

  return (
    <div className="page-container">
      <Card title="订单校验（上传发货后台总表 → 核对金额 / 快递单号 / 遗漏）" style={{ marginBottom: 16 }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={parseFile}>
            <Button icon={<UploadOutlined />} type="primary" loading={loading}>上传发货总表</Button>
          </Upload>
          {result && <Button icon={<ReloadOutlined />} onClick={() => setResult(null)}>清除结果</Button>}
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>
            自动识别「订单号 / 实收 / 运单号」三列，与订单管理数据核对，只提示不修改任何订单
          </span>
        </Space>

        {!result && (
          <Alert type="info" showIcon icon={<FileExcelOutlined />} message="使用说明"
            description="每日订单导入后，过一段时间从发货后台导出总表，上传到这里核对。重点看三类问题：① 遗漏（总表有、系统没有的订单）；② 金额错误（总表实收 ≠ 系统实收价）；③ 单号错误（总表运单号 ≠ 系统快递单号）。" />
        )}

        {result && (
          <>
            <Alert type={result.missing_count || result.amount_err_count || result.track_err_count ? 'warning' : 'success'} showIcon style={{ marginBottom: 16 }}
              message={`校验完成：总表 ${result.bill_count} 单，匹配 ${result.matched_count} 单`}
              description={
                <Space wrap>
                  <Tag color="#3F8600">正常 {result.ok_count} 单</Tag>
                  <Tag color="#CF1322">遗漏 {result.missing_count} 单</Tag>
                  <Tag color="#CF1322">金额错误 {result.amount_err_count} 单</Tag>
                  <Tag color="#EF9F27">单号错误 {result.track_err_count} 单</Tag>
                  {result.sys_extra_count > 0 && <Tag>系统多出（总表无）{result.sys_extra_count} 单</Tag>}
                </Space>
              } />
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={5}><Card size="small"><Stat text="总表单数" value={result.bill_count} color="#26201C" /></Card></Col>
              <Col span={5}><Card size="small"><Stat text="正常" value={result.ok_count} color="#389E0D" /></Card></Col>
              <Col span={5}><Card size="small"><Stat text="遗漏" value={result.missing_count} color="#CF1322" /></Card></Col>
              <Col span={5}><Card size="small"><Stat text="金额错误" value={result.amount_err_count} color="#CF1322" /></Card></Col>
              <Col span={4}><Card size="small"><Stat text="单号错误" value={result.track_err_count} color="#D48806" /></Card></Col>
            </Row>
            <Tabs items={tabItems} />
            {result.sys_extra_count > 0 && (
              <Alert type="info" showIcon style={{ marginTop: 12 }}
                message={`系统中有 ${result.sys_extra_count} 个订单号不在总表里（可能是总表时间范围不同，或这些订单是系统多录的）`}
                description={result.sys_extra_nos.join('、')} />
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function Stat({ text, value, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 12, color: '#8c8c8c' }}>{text}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
